/**
 * Local-first chat cache — a tiny, dependency-free IndexedDB layer.
 *
 * Persists the user's chat state (conversations + messages, text only) so the
 * app opens instantly from local storage and works offline; a delta sync then
 * pulls whatever changed on the server since the last visit. Attachments/blobs
 * are intentionally NOT stored here (metadata only) — text stays well under any
 * quota (~200 bytes/message → 200 MB ≈ 1M messages).
 *
 * Object stores:
 *   - `messages`      (keyPath id)      — every cached WireMessage; index byConv
 *   - `conversations` (keyPath id)      — WireConversation list (participants +
 *                                         cursors live here)
 *   - `meta`          (keyPath key)     — { userId } guard + { syncWatermark }
 *
 * Everything is best-effort: if IndexedDB is unavailable or a call fails we
 * degrade to the network path (the store still works), never throw into the UI.
 */

import type { Conversation as WireConversation, Message as WireMessage } from './messenger.wire'

const DB_NAME = 'inmeet-chat'
const DB_VERSION = 1

let dbPromise: Promise<IDBDatabase | null> | null = null

function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null)
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve) => {
    let req: IDBOpenDBRequest
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION)
    } catch {
      resolve(null)
      return
    }
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains('messages')) {
        const s = db.createObjectStore('messages', { keyPath: 'id' })
        s.createIndex('byConv', 'conversationId', { unique: false })
      }
      if (!db.objectStoreNames.contains('conversations')) {
        db.createObjectStore('conversations', { keyPath: 'id' })
      }
      if (!db.objectStoreNames.contains('meta')) {
        db.createObjectStore('meta', { keyPath: 'key' })
      }
    }
    req.onsuccess = () => {
      const db = req.result
      // Let go when the database is deleted — logging out does that, from
      // this tab or another. Held open, the delete is blocked until every tab
      // closes; the next call here opens a fresh one.
      db.onversionchange = () => {
        db.close()
        dbPromise = null
      }
      resolve(db)
    }
    req.onerror = () => {
       
      console.warn('[chat-db] open failed:', req.error)
      resolve(null)
    }
  })
  return dbPromise
}

function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
    tx.onabort = () => reject(tx.error)
  })
}

async function getAll<T>(store: string): Promise<T[]> {
  const db = await openDb()
  if (!db) return []
  try {
    return await promisify(db.transaction(store, 'readonly').objectStore(store).getAll() as IDBRequest<T[]>)
  } catch {
    return []
  }
}

async function putAll<T>(store: string, items: T[]): Promise<void> {
  if (items.length === 0) return
  const db = await openDb()
  if (!db) return
  try {
    const tx = db.transaction(store, 'readwrite')
    const os = tx.objectStore(store)
    for (const it of items) os.put(it)
    await txDone(tx)
  } catch {
    /* best-effort */
  }
}

// ── Public API ──────────────────────────────────────────────────────────────

export interface StoredMeta { key: string; value: string }

export async function getMeta(key: string): Promise<string | null> {
  const db = await openDb()
  if (!db) return null
  try {
    const row = await promisify(
      db.transaction('meta', 'readonly').objectStore('meta').get(key) as IDBRequest<StoredMeta | undefined>,
    )
    return row?.value ?? null
  } catch {
    return null
  }
}

export async function setMeta(key: string, value: string): Promise<void> {
  await putAll<StoredMeta>('meta', [{ key, value }])
}

export const cacheMessages = (msgs: WireMessage[]): Promise<void> => putAll('messages', msgs)
export const cacheConversations = (convs: WireConversation[]): Promise<void> => putAll('conversations', convs)
export const readAllMessages = (): Promise<WireMessage[]> => getAll<WireMessage>('messages')
export const readAllConversations = (): Promise<WireConversation[]> => getAll<WireConversation>('conversations')

/**
 * Drop this conversation's cached messages, optionally sparing `keepIds`.
 *
 * The local half of Clear Chat and Delete Chat. Without it the UI emptied the
 * thread in memory only, and the very next reload hydrated it straight back out
 * of IndexedDB — the chat "came back on refresh" even after the server had
 * agreed it was gone.
 *
 * Walks the `byConv` index with a cursor rather than reading the store and
 * putting back the survivors: the delete happens inside one transaction, so a
 * concurrent write-through flush can't interleave and resurrect a row.
 */
export async function purgeConversationMessages(
  conversationId: string,
  keepIds: readonly string[] = [],
): Promise<void> {
  const db = await openDb()
  if (!db) return
  const keep = keepIds.length > 0 ? new Set(keepIds) : null
  try {
    const tx = db.transaction('messages', 'readwrite')
    const idx = tx.objectStore('messages').index('byConv')
    const req = idx.openCursor(IDBKeyRange.only(conversationId))
    req.onsuccess = () => {
      const cursor = req.result
      if (!cursor) return
      const row = cursor.value as WireMessage
      if (!keep || !keep.has(row.id)) cursor.delete()
      cursor.continue()
    }
    await txDone(tx)
  } catch {
    /* best-effort */
  }
}

/**
 * Drop one message from the local cache.
 *
 * "Delete for me" removes a message from the caller's copy of a thread, and
 * this cache IS that copy — the thread renders from here on load, before any
 * request goes out. Without this the hide was written on the server, the
 * message vanished from the screen, and the next refresh read it straight back
 * out of IndexedDB: deleted everywhere except the one place the user was
 * looking.
 */
export async function purgeMessage(messageId: string): Promise<void> {
  const db = await openDb()
  if (!db) return
  try {
    const tx = db.transaction('messages', 'readwrite')
    tx.objectStore('messages').delete(messageId)
    await txDone(tx)
  } catch {
    /* best-effort — the server tombstone is the durable record */
  }
}

/** Purge a conversation entirely — its messages AND its list row (Delete Chat). */
export async function purgeConversation(conversationId: string): Promise<void> {
  await purgeConversationMessages(conversationId)
  const db = await openDb()
  if (!db) return
  try {
    const tx = db.transaction('conversations', 'readwrite')
    tx.objectStore('conversations').delete(conversationId)
    await txDone(tx)
  } catch {
    /* best-effort */
  }
}

/** Wipe the whole cache (user switch / logout). */
export async function clearChatCache(): Promise<void> {
  const db = await openDb()
  if (!db) return
  try {
    const tx = db.transaction(['messages', 'conversations', 'meta'], 'readwrite')
    tx.objectStore('messages').clear()
    tx.objectStore('conversations').clear()
    tx.objectStore('meta').clear()
    await txDone(tx)
  } catch {
    /* best-effort */
  }
}

/**
 * Ask the browser to make our storage persistent so IndexedDB isn't evicted
 * under disk pressure. Best-effort; ignored where unsupported.
 */
export async function requestPersistentStorage(): Promise<void> {
  try {
    if (navigator.storage?.persist && !(await navigator.storage.persisted())) {
      await navigator.storage.persist()
    }
  } catch {
    /* ignore */
  }
}
